// src/components/tools/communications/MessagingTool.jsx
// Messaging & Communications Tool: Email, WhatsApp, Internal Social Feed
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Plus, Search, Mail, Send, Check, X, ChevronRight, Hash, Save, AlertCircle, Image,
  Settings, Banknote, Users, Zap, Grid, List, Calendar, Trash2, Edit2, 
  Code, Paperclip, ClipboardList, RefreshCw, Loader2, Sparkles, Phone,
  Facebook, Instagram, Linkedin, MessageSquare, ExternalLink, Globe,
  LayoutTemplate, Copy
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions'; // For Cloud Function Calls

// Mock/Shared UI Components (assuming availability or simple styling)
const Card = ({ children, className = '' }) => <div className={`bg-white rounded-xl border border-gray-100 ${className}`}>{children}</div>;
const Button = ({ children, onClick, variant = 'primary', size = 'md', className = '', disabled, isLoading }) => (
  <button
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`flex items-center justify-center gap-2 font-medium transition-all rounded-lg 
      ${size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}
      ${variant === 'primary' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 
        variant === 'secondary' ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 
        'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}
      ${(disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
  >
    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
    {children}
  </button>
);
const Input = ({ label, value, onChange, placeholder = '', type = 'text', required = false, className = '' }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700">{label}{required && ' *'}</label>}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm ${className}`}
    />
  </div>
);
const TextArea = ({ label, value, onChange, placeholder = '', rows = 3, className = '' }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm ${className}`}
    />
  </div>
);
const Select = ({ label, value, onChange, options, className = '' }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
    <select
      value={value}
      onChange={onChange}
      className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-sm bg-white ${className}`}
    >
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

// --- CONSTANTS ---
const ViewState = {
  DASHBOARD: 'DASHBOARD',
  EMAIL_TEMPLATES: 'EMAIL_TEMPLATES',
  EMAIL_COMPOSER: 'EMAIL_COMPOSER',
  WHATSAPP_SENDER: 'WHATSAPP_SENDER',
  SOCIAL_FEED: 'SOCIAL_FEED',
  SETTINGS: 'SETTINGS',
  TEMPLATE_EDITOR: 'TEMPLATE_EDITOR'
};

const MessageType = {
  EMAIL: 'EMAIL',
  WHATSAPP: 'WHATSAPP',
  SOCIAL: 'SOCIAL'
};

const MessageStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED'
};

// Mock data (replace with actual fetch/save to Firestore)
const mockMessages = [
  { id: 'm1', type: MessageType.EMAIL, to: 'client@mail.com', content: 'Design update', status: MessageStatus.SENT, sentAt: new Date().toISOString() },
  { id: 'm2', type: MessageType.WHATSAPP, to: '27831234567', content: 'Invoice link sent', status: MessageStatus.SENT, sentAt: new Date().toISOString() },
  { id: 'm3', type: MessageType.SOCIAL, socialGroup: 'internal-team', content: 'New marketing strategy deployed.', status: MessageStatus.SENT, sentAt: new Date().toISOString() },
  { id: 'm4', type: MessageType.EMAIL, to: 'test@mail.com', content: 'Failed campaign send', status: MessageStatus.FAILED, sentAt: new Date().toISOString() },
];

const mockTemplates = [
  { id: 't1', name: 'Welcome Client', html: '<h1>Welcome!</h1><p>Thank you for joining us.</p>', variables: ['name', 'company'], thumbnailUrl: '' },
  { id: 't2', name: 'Invoice Overdue', html: '<h2>Urgent Notice</h2><p>Your invoice is now overdue.</p>', variables: ['invoiceId', 'dueDate'], thumbnailUrl: '' },
];

const mockSocialPosts = [
  { id: 'p1', authorId: 'user1', content: 'Great quarter everyone! Let\'s keep the momentum going. #team #success', createdAt: new Date().toISOString(), media: [] },
  { id: 'p2', authorId: 'user2', content: 'Need feedback on the new landing page. Link in comments.', createdAt: new Date(Date.now() - 3600000).toISOString(), media: [] },
];

// --- UI COMPONENTS ---

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100 shadow-sm`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const DocumentItem = ({ icon: Icon, title, description, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-left"
  >
    <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
      <Icon className="w-5 h-5 text-indigo-600" />
    </div>
    <div className="flex-1">
      <p className="font-medium text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 truncate">{description}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-gray-400" />
  </button>
);

// --- VIEWS ---

const MessagingDashboard = ({ stats, onNavigate }) => (
  <div className="max-w-7xl mx-auto p-6 space-y-6">
    <h2 className="text-xl font-bold text-gray-900">Communication Dashboard</h2>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard icon={Send} label="Emails Sent" value={stats.emailsSent} color="indigo" onClick={() => onNavigate(ViewState.EMAIL_COMPOSER)} />
      <StatCard icon={Phone} label="WhatsApp Messages" value={stats.whatsappCount} color="emerald" onClick={() => onNavigate(ViewState.WHATSAPP_SENDER)} />
      <StatCard icon={MessageSquare} label="Social Posts" value={stats.socialPosts} color="purple" onClick={() => onNavigate(ViewState.SOCIAL_FEED)} />
      <StatCard icon={LayoutTemplate} label="Templates" value={stats.templates} color="amber" onClick={() => onNavigate(ViewState.EMAIL_TEMPLATES)} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-6 lg:col-span-2">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Quick Send</h3>
        <div className="space-y-3">
          <DocumentItem
            icon={Mail}
            title="Compose Email"
            description="Send a designer email using a template"
            onClick={() => onNavigate(ViewState.EMAIL_COMPOSER)}
          />
          <DocumentItem
            icon={Phone}
            title="WhatsApp Message"
            description="Send a message instantly via wa.me link"
            onClick={() => onNavigate(ViewState.WHATSAPP_SENDER)}
          />
          <DocumentItem
            icon={Facebook}
            title="Internal Social Post"
            description="Post an update to the team's internal feed"
            onClick={() => onNavigate(ViewState.SOCIAL_FEED)}
          />
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Settings className="w-5 h-5 text-gray-600" /> Configuration</h3>
        <div className="space-y-3">
          <DocumentItem
            icon={Settings}
            title="SMTP & Social Settings"
            description="Configure email credentials and external social tokens"
            onClick={() => onNavigate(ViewState.SETTINGS)}
          />
          <DocumentItem
            icon={LayoutTemplate}
            title="Manage Templates"
            description="Create and edit reusable email and WhatsApp templates"
            onClick={() => onNavigate(ViewState.EMAIL_TEMPLATES)}
          />
        </div>
      </Card>
    </div>
  </div>
);

const EmailTemplateEditor = ({ template, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState(template || { name: '', html: '<h1>[Template Name]</h1><p>Content goes here. Use the sidebar to insert variables.</p>', variables: [] });
  const [newVar, setNewVar] = useState('');

  const handleAddVariable = () => {
    if (newVar.trim() && !formData.variables.includes(newVar.trim())) {
      setFormData(prev => ({ ...prev, variables: [...prev.variables, newVar.trim()] }));
      setNewVar('');
    }
  };

  return (
    <Card className="max-w-6xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6 border-b pb-4">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{template?.id ? 'Edit' : 'Create'} Email Template</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings & Variables Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <Input label="Template Name" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} required />
          
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Merge Variables</h3>
            <div className="space-y-2">
              {formData.variables.map(v => (
                <div key={v} className="flex justify-between items-center bg-gray-50 p-2 rounded text-sm font-mono">
                  
                  <button onClick={() => setFormData(p => ({ ...p, variables: p.variables.filter(item => item !== v) }))} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newVar}
                onChange={e => setNewVar(e.target.value)}
                placeholder="Variable name (e.g. name)"
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <Button onClick={handleAddVariable} size="sm" disabled={!newVar.trim()}>Add</Button>
            </div>
            <p className="text-xs text-gray-500 pt-2 border-t">Copy the variable format e.g., **{'{'}{'{'}name{'}'}{'}'}** into the HTML.</p>
          </Card>
        </div>

        {/* HTML Editor & Preview */}
        <div className="lg:col-span-2 space-y-4">
          <TextArea label="Template HTML" rows={15} value={formData.html} onChange={e => setFormData(p => ({ ...p, html: e.target.value }))} placeholder="Paste your designer HTML here..." />
          
          <Card className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Live Preview</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden min-h-[300px] bg-white">
              <iframe
                title="Email Preview"
                sandbox=""
                srcDoc={formData.html}
                className="w-full h-[300px] border-none"
              />
            </div>
          </Card>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button onClick={() => onSave(formData)} isLoading={saving} disabled={!formData.name || !formData.html}>
              <Save className="w-4 h-4" /> Save Template
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const EmailComposer = ({ templates, settings, clients, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ to: '', subject: '', content: '', templateId: '', attachments: [] });
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const selectedTemplate = templates.find(t => t.id === formData.templateId);
  const isSmtpConfigured = settings?.smtpHost && settings?.emailUser && settings?.emailPass;

  const handleSend = async () => {
    if (!isSmtpConfigured) {
      alert("SMTP credentials are not configured. Please go to Settings.");
      return;
    }
    if (!formData.to || !formData.subject || !formData.content) {
      alert("Recipient, Subject, and Content are required.");
      return;
    }
    
    setIsSending(true);
    setSendResult(null);

    try {
      // Simulate Cloud Function call (replace with actual call to getFunctions().httpsCallable('sendEmail'))
      await new Promise(resolve => setTimeout(resolve, 2000)); 
      
      const emailContent = selectedTemplate ? formData.content : `<h1>${formData.subject}</h1>${formData.content}`;

      // Actual save to message history
      await onSave({
        type: MessageType.EMAIL,
        to: formData.to,
        subject: formData.subject,
        content: emailContent,
        templateId: formData.templateId || null,
        status: MessageStatus.SENT, // Assuming success from Cloud Function
      });
      
      setSendResult({ success: true, message: "Email queued for sending via Cloud Function." });
    } catch (error) {
      console.error('Email send failed:', error);
      setSendResult({ success: false, message: error.message || "Failed to send email." });
    } finally {
      setIsSending(false);
    }
  };

  const handleContentUpdate = (e) => {
    // If a template is selected, this input should only be for variable/text content
    if (selectedTemplate) {
      // Simple logic to keep the original HTML and replace a mock variable
      const mockHtml = selectedTemplate.html.replace('<p>Content goes here.</p>', `<p>${e.target.value}</p>`);
      setFormData(p => ({ ...p, content: e.target.value, finalHtml: mockHtml }));
    } else {
      setFormData(p => ({ ...p, content: e.target.value }));
    }
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    const template = templates.find(t => t.id === templateId);
    setFormData(p => ({ 
      ...p, 
      templateId, 
      content: template ? template.html : p.content // Initial content from template
    }));
  };

  return (
    <Card className="max-w-6xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6 border-b pb-4">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Email Composer</h1>
      </div>
      
      {!isSmtpConfigured && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          SMTP credentials not configured. Email sending is disabled.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer Form */}
        <div className="lg:col-span-1 space-y-4">
          <Input label="To (Email)" type="email" value={formData.to} onChange={e => setFormData(p => ({ ...p, to: e.target.value }))} required placeholder="client@example.com" />
          <Input label="Subject" value={formData.subject} onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))} required />
          
          <Select label="Template" value={formData.templateId} onChange={handleTemplateSelect} options={[
            { value: '', label: 'No Template (Raw HTML/Text)' },
            ...templates.map(t => ({ value: t.id, label: t.name }))
          ]} />

          {selectedTemplate ? (
             <TextArea label="Body Text / Variable Content" rows={6} value={formData.content} onChange={handleContentUpdate} placeholder="Enter text to replace template variables..." />
          ) : (
            <TextArea label="Email Body (HTML or Plain Text)" rows={10} value={formData.content} onChange={e => setFormData(p => ({ ...p, content: e.target.value }))} placeholder="Type your email content here (HTML supported if template is not used)..." />
          )}

          <Button variant="secondary" size="sm" className="w-full"><Paperclip className="w-4 h-4" /> Add Attachment (Future)</Button>
        </div>

        {/* Preview & Actions */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Preview</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden min-h-[300px] bg-white">
              <iframe
                title="Email Preview"
                sandbox=""
                srcDoc={formData.finalHtml || formData.content}
                className="w-full h-[300px] border-none"
              />
            </div>
          </Card>
          
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSend} isLoading={isSending} disabled={!isSmtpConfigured || !formData.to || !formData.subject || !formData.content}>
              <Send className="w-4 h-4" /> {isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>

          {sendResult && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${sendResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {sendResult.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {sendResult.message}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const WhatsAppMessageForm = ({ clients, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ phone: '', message: '' });

  const handleOpenWhatsApp = () => {
    if (!formData.phone || !formData.message) {
      alert("Phone number and message are required.");
      return;
    }
    
    // Clean phone number (e.g., remove +, -, spaces) and ensure it has country code
    const cleanedPhone = formData.phone.replace(/[^0-9+]/g, '');
    const encodedMessage = encodeURIComponent(formData.message);
    const whatsappUrl = `https://wa.me/${cleanedPhone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Save to message history
    onSave({
      type: MessageType.WHATSAPP,
      to: cleanedPhone,
      content: formData.message,
      status: MessageStatus.SENT,
    });
  };

  return (
    <Card className="max-w-xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6 border-b pb-4">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Sender</h1>
      </div>

      <Input label="Recipient Phone (e.g. 27831234567)" type="tel" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} required placeholder="Include country code (no + or spaces)" />
      
      <TextArea label="Message" rows={6} value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} required className="mt-4" placeholder="Plain text message. Markdown (bold, italic) is supported by WhatsApp." />
      
      {/* Client Picker - Simplified */}
      <Select label="Pick Client (Future CRM Integration)" value={''} onChange={e => {
        const client = clients.find(c => c.id === e.target.value);
        if (client?.phone) {
          setFormData(p => ({ ...p, phone: client.phone.replace(/[^0-9+]/g, '') }));
        }
      }} 
        options={[{ value: '', label: 'Select from CRM Contacts (Future)' }, ...clients.map(c => ({ value: c.id, label: `${c.name} (${c.phone || 'No Phone'})` }))]}
        className="mt-4"
      />
      
      <div className="flex justify-end pt-6 border-t mt-6">
        <Button onClick={handleOpenWhatsApp} disabled={!formData.phone || !formData.message} className="bg-green-600 hover:bg-green-700">
          <ExternalLink className="w-4 h-4" /> Open in WhatsApp
        </Button>
      </div>
    </Card>
  );
};

const SocialFeed = ({ posts, currentUserId, onPost, onDelete }) => {
  const [newPost, setNewPost] = useState('');
  const [isPosting, setIsPosting] = useState(false);

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setIsPosting(true);
    try {
      await onPost({
        authorId: currentUserId,
        content: newPost,
        media: [],
        createdAt: new Date().toISOString()
      });
      setNewPost('');
    } catch(e) {
      alert("Failed to create post.");
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <h2 className="text-xl font-bold text-gray-900">Internal Social Feed</h2>
      <Card className="p-4 space-y-4">
        <TextArea rows={3} placeholder="What's the latest update for the team/workspace?" value={newPost} onChange={e => setNewPost(e.target.value)} />
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="text-sm text-gray-500 flex items-center gap-2">
             <Image className="w-4 h-4" /> Add Image (Future)
          </span>
          <Button onClick={handlePost} isLoading={isPosting} disabled={!newPost.trim()}>
            <Send className="w-4 h-4" /> Post Update
          </Button>
        </div>
      </Card>
      
      <h3 className="font-semibold text-gray-900">Feed</h3>
      <div className="space-y-4">
        {[...posts].sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).map(post => (
          <Card key={post.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900">User {post.authorId}</p>
                <p className="text-xs text-gray-500">{new Date(post.createdAt).toLocaleString()}</p>
              </div>
              {post.authorId === currentUserId && (
                <button onClick={() => onDelete(post.id)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="mt-3 text-gray-700 whitespace-pre-wrap">{post.content}</p>
            <div className="flex items-center gap-3 mt-4 pt-3 border-t">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-500">0 Comments (Future)</span>
            </div>
          </Card>
        ))}
        {posts.length === 0 && <p className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">No posts yet. Share an update!</p>}
      </div>
    </div>
  );
};

const MessagingSettings = ({ settings, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    smtpHost: '', emailUser: '', emailPass: '', smtpPort: 587, senderName: '',
    // Social Placeholders
    facebookPageId: '', facebookAccessToken: '',
    ...settings
  });

  return (
    <Card className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6 border-b pb-4">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Messaging Settings</h1>
      </div>
      
      <div className="space-y-6">
        {/* SMTP Settings */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> SMTP Configuration (Emails)</h2>
          <Input label="Sender Name" value={formData.senderName} onChange={e => setFormData(p => ({ ...p, senderName: e.target.value }))} placeholder="e.g. Your Company Name" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="SMTP Host" value={formData.smtpHost} onChange={e => setFormData(p => ({ ...p, smtpHost: e.target.value }))} placeholder="e.g. smtp.gmail.com" />
            <Input label="SMTP Port" type="number" value={formData.smtpPort} onChange={e => setFormData(p => ({ ...p, smtpPort: parseInt(e.target.value) || 587 }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Email User" value={formData.emailUser} onChange={e => setFormData(p => ({ ...p, emailUser: e.target.value }))} placeholder="Your email address" />
            <Input label="Email Password/App Key" type="password" value={formData.emailPass} onChange={e => setFormData(p => ({ ...p, emailPass: e.target.value }))} placeholder="Securely stored" />
          </div>
          <p className="text-xs text-gray-500 pt-2 border-t">Warning: Your email password/app key is stored securely in Firebase, but use an App-Specific Password for security.</p>
        </Card>
        
        {/* Social Settings (Placeholders) */}
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Globe className="w-5 h-5 text-purple-600" /> External Social Accounts (Future)</h2>
          <p className="text-sm text-gray-600">Connect your accounts for external posting (Requires OAuth token flows)</p>
          <div className="grid grid-cols-3 gap-4">
            <Button variant="secondary" disabled><Facebook className="w-4 h-4" /> Connect Facebook</Button>
            <Button variant="secondary" disabled><Instagram className="w-4 h-4" /> Connect Instagram</Button>
            <Button variant="secondary" disabled><Linkedin className="w-4 h-4" /> Connect LinkedIn</Button>
          </div>
          <p className="text-xs text-gray-500 pt-2 border-t">OAuth tokens will be stored securely here.</p>
        </Card>
        
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button onClick={onCancel} variant="secondary">Cancel</Button>
          <Button onClick={() => onSave(formData)} isLoading={saving}>
            <Save className="w-4 h-4" /> Save Settings
          </Button>
        </div>
      </div>
    </Card>
  );
};


// --- MAIN COMPONENT ---
const MessagingTool = ({ bookId, customStyling = {}, toolsService, onBack, user }) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [settings, setSettings] = useState({});
  const [clients, setClients] = useState([]); // Mock clients for contact picker
  
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // NOTE: Using general getToolData as specific messaging service methods are not defined
      const [msgData, tmplData, postData, settingsData, clientData] = await Promise.all([
        toolsService.getToolData(bookId, 'messages'),
        toolsService.getToolData(bookId, 'email-templates'),
        toolsService.getToolData(bookId, 'social-posts'),
        toolsService.getToolSettings(bookId, 'messaging-settings'), // Using getToolSettings for secure settings
        toolsService.getToolData(bookId, 'clients') // Assuming client data exists in this slug for contact picker
      ]);
      
      setMessages(msgData || mockMessages);
      setTemplates(tmplData || mockTemplates);
      setSocialPosts(postData || mockSocialPosts);
      setSettings(settingsData || {});
      setClients(clientData || []); // Mock clients if none exist
    } catch (error) {
      console.error('Error loading messaging data:', error);
      // Fallback to mocks if Firestore access fails
      setMessages(mockMessages);
      setTemplates(mockTemplates);
      setSocialPosts(mockSocialPosts);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveData = async (slug, data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, slug, data);
      await loadData();
    } catch (error) {
      console.error(`Error saving ${slug} data:`, error);
      alert(`Failed to save ${slug}.`);
      throw error;
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveSettings = async (data) => {
    setSaving(true);
    try {
      // NOTE: For a real app, emailPass should be handled by a secure Cloud Function endpoint, 
      // not saved directly via toolsService.saveToolSettings (which uses setDoc, NOT encryption).
      // For this implementation, we save as non-secure settings.
      await toolsService.saveToolSettings(bookId, 'messaging-settings', data);
      await loadData();
      setViewState(ViewState.DASHBOARD);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteData = async (slug, id) => {
    setSaving(true);
    try {
      await toolsService.deleteToolData(bookId, slug, id);
      await loadData();
    } catch (error) {
      console.error(`Error deleting ${slug} data:`, error);
      alert(`Failed to delete ${slug}.`);
    } finally {
      setSaving(false);
    }
  };


  // Calculate dashboard stats
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const emails = messages.filter(m => m.type === MessageType.EMAIL);
    const todayEmails = emails.filter(m => m.sentAt?.includes(today));
    const whatsapp = messages.filter(m => m.type === MessageType.WHATSAPP);
    
    return {
      emailsSent: todayEmails.length,
      whatsappCount: whatsapp.length,
      socialPosts: socialPosts.length,
      templates: templates.length
    };
  }, [messages, socialPosts, templates]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  // --- View Renderer ---
  const handleNavigate = (view, data = null) => {
    if (view === ViewState.TEMPLATE_EDITOR) {
      setEditingTemplate(data);
      setViewState(ViewState.TEMPLATE_EDITOR);
    } else {
      setViewState(view);
    }
  };

  switch (viewState) {
    case ViewState.EMAIL_COMPOSER:
      return (
        <EmailComposer 
          templates={templates}
          settings={settings}
          clients={clients}
          onSave={(data) => handleSaveData('messages', data)}
          onCancel={() => setViewState(ViewState.DASHBOARD)}
        />
      );
      
    case ViewState.WHATSAPP_SENDER:
      return (
        <WhatsAppMessageForm 
          clients={clients}
          onSave={(data) => handleSaveData('messages', data)}
          onCancel={() => setViewState(ViewState.DASHBOARD)}
        />
      );
      
    case ViewState.SOCIAL_FEED:
      return (
        <div className="h-full overflow-y-auto">
          <button onClick={() => setViewState(ViewState.DASHBOARD)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-6 py-4">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <SocialFeed 
            posts={socialPosts}
            currentUserId={user?.uid || 'user1'}
            onPost={(data) => handleSaveData('social-posts', data)}
            onDelete={(id) => handleDeleteData('social-posts', id)}
          />
        </div>
      );
      
    case ViewState.EMAIL_TEMPLATES:
      return (
        <div className="h-full overflow-y-auto">
          <Card className="max-w-6xl mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Email Templates ({templates.length})</h1>
              </div>
              <Button onClick={() => handleNavigate(ViewState.TEMPLATE_EDITOR, null)}>
                <Plus className="w-4 h-4" /> New Template
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.id} className="p-4 space-y-3">
                  <h3 className="font-semibold text-gray-900">{t.name}</h3>
                  <div className="h-24 bg-gray-50 rounded-lg overflow-hidden relative">
                    <iframe title={`Preview ${t.name}`} sandbox="" srcDoc={t.html} className="w-full h-full transform scale-50 origin-top-left border-none" />
                  </div>
                  <div className="flex justify-between gap-2">
                    <Button onClick={() => handleNavigate(ViewState.TEMPLATE_EDITOR, t)} size="sm" variant="secondary">
                      <Edit2 className="w-4 h-4" /> Edit
                    </Button>
                    <Button onClick={() => handleDeleteData('email-templates', t.id)} size="sm" variant="secondary" className="text-red-500 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" /> Delete
                    </Button>
                    <Button onClick={() => setViewState(ViewState.EMAIL_COMPOSER)} size="sm">
                       <Copy className="w-4 h-4" /> Use
                    </Button>
                  </div>
                </Card>
              ))}
              {templates.length === 0 && <p className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg col-span-full">No templates yet. Create one!</p>}
            </div>
          </Card>
        </div>
      );
      
    case ViewState.TEMPLATE_EDITOR:
      return (
        <div className="h-full overflow-y-auto">
          <EmailTemplateEditor 
            template={editingTemplate}
            onSave={(data) => handleSaveData('email-templates', data).then(() => setViewState(ViewState.EMAIL_TEMPLATES))}
            onCancel={() => setViewState(ViewState.EMAIL_TEMPLATES)}
            saving={saving}
          />
        </div>
      );
      
    case ViewState.SETTINGS:
      return (
        <div className="h-full overflow-y-auto">
          <MessagingSettings 
            settings={settings}
            onSave={handleSaveSettings}
            onCancel={() => setViewState(ViewState.DASHBOARD)}
            saving={saving}
          />
        </div>
      );

    case ViewState.DASHBOARD:
    default:
      return (
        <div className="h-full overflow-y-auto">
          <div className="flex items-center justify-between p-6 pb-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
               <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
                 <ArrowLeft className="w-5 h-5 text-gray-600" />
               </button>
               Messaging & Communications
            </h1>
            <Button onClick={() => setViewState(ViewState.SETTINGS)} variant="secondary" size="sm">
              <Settings className="w-4 h-4" /> Settings
            </Button>
          </div>
          <MessagingDashboard stats={stats} onNavigate={handleNavigate} />
        </div>
      );
  }
};

export default MessagingTool;